import React from 'react';

import { Nullable } from '@clearverify/types';

import {
  useVerifierQuery,
  useVerifierUpdateMutation,
  VerifierDocument,
  VerifierType,
  VerifierUpdate,
} from 'src/api';

import { useNotification } from '../app';

export const useVerifierById = (verifierId: Nullable<string>) => {
  const { createApolloContext } = useNotification();

  const { data, loading } = useVerifierQuery({
    variables: {
      id: String(verifierId),
    },
    skip: !verifierId,
  });

  const verifier = React.useMemo(() => data?.verifier, [data?.verifier]);

  const verifierType = verifier?.type;

  const isCommercialVerifier = React.useMemo(() => {
    return verifier?.type === VerifierType.Commercial;
  }, [verifier?.type]);

  const isGovernmentVerifier = React.useMemo(() => {
    return verifier?.type === VerifierType.Government;
  }, [verifier?.type]);

  const verifierStripeCustomerId = React.useMemo(() => {
    return verifier?.stripeCustomerId;
  }, [verifier?.stripeCustomerId]);

  const [verifierUpdateMutation, { loading: updating }] = useVerifierUpdateMutation({
    context: createApolloContext({
      errorMessage: 'Failed to update verifier',
      successMessage: 'Verifier has been successfully updated',
    }),
  });

  const updateVerifier = async (data: VerifierUpdate) => {
    if (!verifierId) return;

    await verifierUpdateMutation({
      variables: {
        id: verifierId,
        data,
      },
      refetchQueries: [
        {
          query: VerifierDocument,
          variables: { id: verifierId },
        },
      ],
    });
  };

  return {
    verifier,
    verifierId,
    verifierType,
    isCommercialVerifier,
    isGovernmentVerifier,
    verifierStripeCustomerId,
    updateVerifier,
    updating,
    loading,
  };
};
